import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PlatformFraudRuleSetSummary } from "@tdelivr/api-client/generated/risk/fraud-rules.js";
import type { PlatformRiskCaseSummary } from "@tdelivr/api-client/generated/risk/risk-case.js";

import { boundedNote } from "../board.ts";
import {
  CREATE_REFUSAL,
  FRAUD_RULES_SCOPE,
  RISK_CASES_SCOPE,
  riskCaseRow,
  ruleSetNote,
  statesPresent,
  stateOf,
} from "../risk-fraud.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `risk` in `16 WP15 Platform Machinery.dc.html`: the risk and fraud board.
 *
 * ```
 * risk:['Risk and fraud','RSK-01…06','Signals, decisions, cases, rules and the blocklist.',
 *   [['Open cases','9','human','to review'],['Blocked today','14','failed','decisions'],
 *    ['Rules','37','done','active'],['Blocklist','212','closed','entries']],
 *   ['Case','Signal','Decision'],
 *   [['RSK-C-2201','5 cards in 10 min','block','human','awaiting analyst'],
 *    ['RSK-C-2198','device shared by 4 accounts','review','human','awaiting analyst'],
 *    ['RSK-C-2190','promo abuse','allow','done','closed']],
 *   'info','Blocklist entries show a reason and an expiry, never just a flag.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                       | source                                                     |
 * | ------------------------------------------- | ---------------------------------------------------------- |
 * | the rows                                    | `RSK-05 GET /v1/platform/risk-cases` (ADR 0070)            |
 * | `Case` — `RSK-C-2201`                       | `riskCases.caseId`, a UUID. Q-212 on the width             |
 * | `Signal` — `5 cards in 10 min`              | **nothing.** RSK-01 publishes no listing (ADR 0072). Q-210 |
 * | `Decision` — `block`                        | **nothing.** RSK-03's, and RSK-03 is no owner. Q-211       |
 * | `human · awaiting analyst`, `done · closed` | `riskCases.state`                                          |
 * | the rules behind the source chip            | `RSK-02 GET /v1/platform/fraud-rule-sets` (ADR 0071)       |
 * | the four KPI tiles                          | **nothing** — R-04, Q-178, Q-213                           |
 * | `Add a risk rule`                           | **drawn and refused.** `CREATE_REFUSAL`, ADR 0071 §6       |
 *
 * ## This page reaches `degraded`, and that is the expected outcome rather than a shortfall
 *
 * Four services own this page. **Two published a listing and two refused to publish one**, by
 * decision and with the reasons measured: ADR 0072 refuses a risk-signal listing because a signal is
 * an assertion of suspicion about an identified person, and ADR 0073 refuses a blocklist listing
 * because `subject_hash` is a lookup key rather than an anonymisation. So two of the grid's three
 * columns draw the design's own `—` and will keep drawing it — this is not work nobody got to, which
 * is the exact confusion both of those records exist to prevent.
 *
 * `../risk-fraud.ts` carries the column-by-column argument, including why `Decision` is **not**
 * filled from the published `disposition`: the design's vocabulary is RSK-03's and `disposition` is a
 * different enum, so a cell headed `Decision` showing one would be a wrong answer rather than a
 * missing one.
 *
 * ## Both scopes are required and only one listing is load-bearing
 *
 * Both are asked for before either call, so a missing grant names both scopes in one actionable
 * sentence. After that the two listings are not equal and are not treated as equal:
 *
 *   - **RSK-05 is the grid.** A failure there is the board's failure state; there is nothing to draw.
 *   - **RSK-02 is one sentence under the grid.** A failure there degrades that sentence and keeps
 *     every risk case on screen. The alternative — one `Promise.all` and one error state — would
 *     hide every risk case on the platform because a second service was unreachable, on the one page
 *     an operator opens *because* something is wrong. `ui/EventCatalogue.tsx` is the same asymmetry
 *     for the same reason.
 *
 * The rule sets are a sentence rather than panel lines because **there is nothing to join them
 * with**: a risk case carries no `policyKey`, no rule identifier and no policy version, and the one
 * field that would point at a rule — `source_reference` — is withheld as a re-identifier of the
 * subject. ADR 0071's own consequence says the board gains no drawable element from that listing.
 */

const riskCases = services.risk__risk_case;
const fraudRules = services.risk__fraud_rules;

export interface RiskFraudProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Risk and data › Risk and fraud`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Risk and fraud";
/**
 * The design's own source chip, word for word: `RSK-01…06`.
 *
 * Six services in one range, of which **two answer**. The chip is the design's copy and is drawn as
 * written — `CLAUDE.md` §1 keeps it word for word — and the footnote under the grid is where the
 * board says which of them answered what. `navigation.ts` lists four owners for this page;
 * RSK-03 and RSK-04 are inside the design's range and are owners of neither.
 */
const SOURCE = "RSK-01…06";
const DESCRIPTION = "Signals, decisions, cases, rules and the blocklist.";
const COLUMNS = ["Case", "Signal", "Decision"] as const;
const NOTE = "Blocklist entries show a reason and an expiry, never just a flag.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's `FORMS.risk` heading button, word for word. */
const PRIMARY_LABEL = "Add rule";

/**
 * The most rows one request answers, which is both services' own maximum.
 *
 * One request per listing. RSK-02's is asked for at the same bound rather than a smaller one on the
 * argument that it only fills a sentence: the sentence says how many rule-set versions the platform
 * runs, and a page cut short would understate it without saying so. When either answer is truncated
 * the board says so — `boundedNote` for the rows, `ruleSetNote` plus a clause here for the rules.
 */
const PER_FETCH = 200;

export function RiskFraud({ transport, session, crumbs }: RiskFraudProps): ReactNode {
  const [rows, setRows] = useState<readonly PlatformRiskCaseSummary[] | undefined>(undefined);
  const [ruleSets, setRuleSets] = useState<readonly PlatformFraudRuleSetSummary[] | undefined>(
    undefined,
  );
  const [truncated, setTruncated] = useState(false);
  const [rulesTruncated, setRulesTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setRuleSets(undefined);
    setFailure(undefined);

    /*
     * Both scopes asked for before either call, not discovered from a refusal.
     *
     * One state naming both, because that is what an operator can act on: they can ask their
     * administrator for `risk-cases:platform:read` and `fraud-rules:platform:read` by name. A 403
     * from either service renders the same state, so this is not the only guard; it is the one that
     * makes both scopes literals in this application's source, which is what `channel-scopes.mjs`
     * derives the Keycloak grant from. ADR 0049 §3.
     */
    const missing = [RISK_CASES_SCOPE, FRAUD_RULES_SCOPE].filter(
      (scope) => !session.scopes.has(scope),
    );
    if (missing.length > 0) {
      setFailure({ kind: "forbidden", missingScopes: missing });
      return;
    }

    /*
     * `listPlatformRiskCases` and `listPlatformFraudRuleSets` — the platform operations, and on
     * neither service is there a wrong one to call.
     *
     * RSK-05's six business operations are all `/internal/v1` with a `tenantId` on the path, and so
     * are RSK-02's five. ADR 0070 §1 and ADR 0071 §1 left both sets byte-for-byte as they were, so
     * these two methods are the only ones a browser can reach on either service beyond
     * `/v1/service-info`.
     *
     * Started together and awaited apart, which is the asymmetry this file's header argues: the
     * cases are the grid and the rule sets are one sentence under it.
     */
    const casePage = riskCases
      .createRiskCaseClient(transport)
      .listPlatformRiskCases(String(PER_FETCH));
    const rulePage = fraudRules
      .createFraudRulesClient(transport)
      .listPlatformFraudRuleSets(String(PER_FETCH));

    /*
     * The rule listing's rejection is caught here and not left for the `await` below, because an
     * unhandled rejection on a promise started before an earlier `await` threw is an unhandled
     * rejection warning rather than a handled failure.
     */
    const rules = rulePage.then(
      (page) => page,
      () => undefined,
    );

    try {
      const page = await casePage;
      setRows(page.riskCases);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [RISK_CASES_SCOPE]));
      return;
    }

    const answered = await rules;
    setRuleSets(answered?.ruleSets);
    setRulesTruncated(answered !== undefined && answered.nextAfter !== null);
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows
          .filter((summary) => state === "" || stateOf(summary).label === state)
          .map((summary) => riskCaseRow(summary));

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "risk cases",
    "ordered by tenant, then case",
  );
  const span = ruleSetNote(rows, ruleSets);
  const spanWithRuleBound =
    span === undefined
      ? undefined
      : rulesTruncated
        ? `${span} More rule-set versions exist than RSK-02 answered in one page, so that count is a floor.`
        : span;

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No risk case has been opened on any tenant. A row appears here when a risk signal or a risk decision is handed to RSK-05 to investigate."
      primary={{ label: PRIMARY_LABEL, refusal: CREATE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(spanWithRuleBound === undefined ? {} : { footnote: spanWithRuleBound })}
      exportName="risk-and-fraud"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
